import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css';
import SidebarItem from './SidebarItem.js';
import { usePermissions } from '../../hooks/usePermission';
import ConfirmDialog from '../ConfirmDialog';

import DirectoryIcon from '../../assets/directory.png';
import LeadsIcon from '../../assets/lead.png';
import ProjectsIcon from '../../assets/project.png';
import PeopleIcon from '../../assets/people.png';
import ScheduleIcon from '../../assets/schedule.png';
import TimesheetsIcon from '../../assets/timesheet.png';
import HomeIcon from '../../assets/home.png';
import SettingsIcon from '../../assets/setting.png';
import LogoutIcon from '../../assets/logout.png';
import LeaveIcon from '../../assets/leave.png';

export default function Sidebar({ collapsed, onToggle, user, onLogout }) {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showProfileMenu && !e.target.closest(`.${styles.profileContainer}`)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const {
    canViewLeads,
    canAccessProjects,
    canViewPeople,
    canAccessSchedule,
    canAccessTimesheets,
    canAccessDirectory,
    canAccessTimeOff,
  } = usePermissions();

  // Define sidebar items with permission requirements
  const sidebarItems = [
    { 
      label: "Dashboard", 
      icon: HomeIcon, 
      path: "", 
      show: true,
      end: true // Only highlight when exactly on /dashboard/
    },
    { 
      label: "Leads", 
      icon: LeadsIcon, 
      path: "leads",
      show: canViewLeads
    },
    { 
      label: "Projects", 
      icon: ProjectsIcon, 
      path: "projects",
      show: canAccessProjects
    },
    { 
      label: "Schedule", 
      icon: ScheduleIcon, 
      path: "schedule",
      show: canAccessSchedule // view_schedule OR view_own_schedule
    },
    { 
      label: "Timesheets", 
      icon: TimesheetsIcon, 
      path: "timesheets",
      show: canAccessTimesheets // view_timesheets OR view_own_timesheets
    },
    { 
      label: "Leave Manager", 
      icon: LeaveIcon, 
      path: "leave-manager",
      show: canAccessTimeOff
    },
    { 
      label: "People & Resources",
      icon: PeopleIcon,
      path: "people",
      show: canViewPeople
    },
    { 
      label: "Directory", 
      icon: DirectoryIcon, 
      path: "directory",
      show: canAccessDirectory // view_internal_directory OR view_external_directory
    },
  ];

  // Filter items based on permissions
  const visibleItems = sidebarItems.filter(item => item.show);

  // Get user initials for avatar
  const userInitials = user?.name 
    ? user.name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("") || "U"
    : "U";

  return (
    <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.toggle} onClick={onToggle}>
        {collapsed ? '▶' : '◀'}
      </div>

      {/* Main navigation items */}
      <div className={styles.navItems}>
        {visibleItems.map((item, i) => (
          <SidebarItem
            key={i}
            label={item.label}
            icon={item.icon}
            path={item.path}
            collapsed={collapsed}
            end={item.end}
          />
        ))}
      </div>

      {/* Bottom section with profile menu */}
      <div className={styles.bottomSection}>
        {/* User profile with dropdown */}
        <div className={styles.profileContainer}>
          <div 
            className={styles.profileAvatar}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            title={user?.name || 'Profile'}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Profile" className={styles.avatarImg} />
            ) : (
              <span className={styles.avatarFallback}>{userInitials}</span>
            )}
            {!collapsed && <span className={styles.label}>{user?.name || 'Profile'}</span>}
          </div>

          {/* Profile dropdown menu */}
          {showProfileMenu && (
            <div className={styles.profileMenu}>
              <div 
                className={styles.menuItem}
                onClick={() => {
                  navigate('/dashboard/settings');
                  setShowProfileMenu(false);
                }}
              >
                <img src={SettingsIcon} alt="Settings" className={styles.menuIcon} />
                <span>Settings</span>
              </div>
              <div 
                className={styles.menuItem}
                onClick={() => {
                  // TODO: Billing functionality
                  setShowProfileMenu(false);
                }}
              >
                <span className={styles.menuIconPlaceholder}>$</span>
                <span>Billing</span>
              </div>
              <div 
                className={styles.menuItem}
                onClick={() => {
                  // TODO: Product Updates functionality
                  setShowProfileMenu(false);
                }}
              >
                <span className={styles.menuIconPlaceholder}>📦</span>
                <span>Product Updates</span>
              </div>
              <div 
                className={styles.menuItem}
                onClick={() => {
                  // TODO: Terms & Policies functionality
                  setShowProfileMenu(false);
                }}
              >
                <span className={styles.menuIconPlaceholder}>📄</span>
                <span>Terms & Policies</span>
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <div 
          className={styles.bottomItem}
          onClick={() => setShowLogoutConfirm(true)}
          title="Logout"
        >
          <img src={LogoutIcon} alt="Logout" className={`${styles.icon} ${styles.logoutIcon}`} />
          {!collapsed && <span className={styles.label}>Logout</span>}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <ConfirmDialog
          message="Are you sure you want to logout?"
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={() => {
            setShowLogoutConfirm(false);
            onLogout();
          }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
}
